import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAdminCreateSystemNotice,
  getAdminListSystemNoticesQueryKey,
} from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { DEFAULT_LANGUAGE } from '@/constants/languages';
import { useAdminGetProducts } from '@/api/auth/admin/admin';

/**
 * Dialog for creating a new system notice
 *
 * Collects basic information and creates a draft notice,
 * then navigates to the detail page for content editing.
 */
export function AddSystemNoticeDialog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [severity, setSeverity] = useState<string>('Info');
  const [acknowledgementMode, setAcknowledgementMode] = useState<string>('User');

  // Fetch available products
  const { data: productsData, isLoading: isLoadingProducts } = useAdminGetProducts();

  // Default to first product when products load
  useEffect(() => {
    if (productsData?.data?.products && productsData.data.products.length > 0 && !productId) {
      const firstProduct = productsData.data.products[0];
      if (firstProduct.productId) {
        setProductId(firstProduct.productId);
      }
    }
  }, [productsData, productId]);

  const { mutate: createNotice, isPending: isCreating } = useAdminCreateSystemNotice({
    mutation: {
      onSuccess: (response) => {
        toast.success('System notice created successfully');
        setIsOpen(false);
        resetForm();
        queryClient.invalidateQueries({
          queryKey: getAdminListSystemNoticesQueryKey(),
        });
        // Navigate to detail page for content editing
        navigate(`/dashboard/admin/system-notices/${response.data.id}`);
      },
      onError: (error: Error) => {
        logger.error(error, {
          context: 'CreateSystemNotice',
          productId: productId.trim(),
        });
        toast.error('Failed to create system notice. ' + (error.message || 'Unknown error'));
      },
    },
  });

  /**
   * Reset form fields
   */
  const resetForm = () => {
    setProductId('');
    setSeverity('Info');
    setAcknowledgementMode('User');
  };

  /**
   * Handle dialog close
   */
  const handleClose = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetForm();
    }
  };

  /**
   * Create the system notice
   */
  const handleCreate = () => {
    // Create with default start time (now) and a default English content entry
    const now = new Date().toISOString();

    createNotice({
      data: {
        productId,
        severity,
        acknowledgementMode,
        status: 'Draft',
        startsAt: now,
        contents: [
          {
            language: DEFAULT_LANGUAGE,
            title: '',
            summary: '',
            bodyHtml: '',
          },
        ],
      },
    });
  };

  const isValid = !!productId;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Notice
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create System Notice</DialogTitle>
          <DialogDescription>
            Create a new system notice. You can edit content and settings after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Product ID */}
          <div className="space-y-2">
            <Label htmlFor="product-id">Product ID</Label>
            <Select value={productId} onValueChange={setProductId} disabled={isCreating || isLoadingProducts}>
              <SelectTrigger id="product-id">
                <SelectValue placeholder={isLoadingProducts ? "Loading products..." : "Select a product"} />
              </SelectTrigger>
              <SelectContent>
                {productsData?.data?.products
                  ?.filter((product) => product.productId)
                  .map((product) => (
                    <SelectItem key={product.productId} value={product.productId!}>
                      {product.productId}
                    </SelectItem>
                  ))}
                {!isLoadingProducts && (!productsData?.data?.products || productsData.data.products.length === 0) && (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    No products available
                  </div>
                )}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Select the product this notice applies to
            </p>
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label htmlFor="severity">Severity</Label>
            <Select value={severity} onValueChange={setSeverity} disabled={isCreating}>
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Info">Info</SelectItem>
                <SelectItem value="Urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Urgent notices require user acknowledgement
            </p>
          </div>

          {/* Acknowledgement Mode */}
          <div className="space-y-2">
            <Label htmlFor="ack-mode">Acknowledgement Mode</Label>
            <Select
              value={acknowledgementMode}
              onValueChange={setAcknowledgementMode}
              disabled={isCreating}
            >
              <SelectTrigger id="ack-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="User">User</SelectItem>
                <SelectItem value="Org">Organization</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Who needs to acknowledge this notice
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!isValid || isCreating}>
            {isCreating ? 'Creating...' : 'Create Notice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
